import { useEffect, useMemo, useState } from "react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Pagination, PaginationContent, PaginationItem, PaginationLink, PaginationPrevious, PaginationNext } from "@/components/ui/pagination";
import { ShieldAlert, ShieldCheck, ShieldOff, ShieldPlus, Fingerprint, Database, Search, Loader2, AlertTriangle } from "lucide-react";

const PAGE_SIZE = 10;

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "-");

function ReadinessBadge({ ready }) {
  if (ready === true) return <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30 text-[10px]" data-testid="ci-readiness-ready">Indexes Ready</Badge>;
  if (ready === false) return <Badge className="bg-amber-500/15 text-amber-600 border-amber-500/30 text-[10px]" data-testid="ci-readiness-not-ready">Indexes Not Installed</Badge>;
  return <Badge variant="outline" className="text-[10px] text-muted-foreground" data-testid="ci-readiness-unknown">Readiness Unverified</Badge>;
}

// Super Admin → Customer Identity Protection: per-tenant enforcement visibility, ON activation
// (POS-0034 Gate 3) and emergency OFF (WORLD-017). Super Admin is the ONLY role that can flip this
// flag in either direction — there is no Owner/self-service toggle anywhere in the application.
export default function CustomerIdentityPanel() {
  const [rows, setRows] = useState(null);
  const [target, setTarget] = useState(null); // tenant row being disabled
  const [f, setF] = useState({ reason: "", password: "", code: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [result, setResult] = useState(null);
  // POS-0034 Gate 2B: Super Admin read-only duplicate audit (separate from Emergency Disable — no writes).
  const [auditTarget, setAuditTarget] = useState(null); // tenant row being audited
  const [auditData, setAuditData] = useState(null);
  const [auditBusy, setAuditBusy] = useState(false);
  const [auditErr, setAuditErr] = useState("");
  // POS-0034 Gate 3: Super Admin ON activation.
  const [activateTarget, setActivateTarget] = useState(null); // tenant row being activated
  const [activateSnapshot, setActivateSnapshot] = useState(null); // live duplicate-audit preview, warning-only
  const [activateSnapshotBusy, setActivateSnapshotBusy] = useState(false);
  const [af, setAf] = useState({ reason: "", password: "", code: "" });
  const [activateBusy, setActivateBusy] = useState(false);
  const [activateErr, setActivateErr] = useState("");
  const [activateResult, setActivateResult] = useState(null);

  const load = () => api.get("/superadmin/customer-identity/overview").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  // Tenant search + pagination (presentation only — the overview endpoint already returns every
  // POS tenant; filtering and paging happen client-side so existing API/behavior stays unchanged).
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const filteredRows = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows || [];
    return (rows || []).filter((r) => r.business_name.toLowerCase().includes(q) || r.tenant_id.toLowerCase().includes(q));
  }, [rows, search]);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pageRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  useEffect(() => { setPage(1); }, [search]);
  useEffect(() => { if (page > totalPages) setPage(totalPages); }, [totalPages, page]);

  const openDisable = (row) => { setTarget(row); setResult(null); setErr(""); setF({ reason: "", password: "", code: "" }); };
  const closeDialog = () => { setTarget(null); setResult(null); setErr(""); };

  const openActivate = async (row) => {
    setActivateTarget(row); setActivateResult(null); setActivateErr(""); setAf({ reason: "", password: "", code: "" });
    setActivateSnapshot(null); setActivateSnapshotBusy(true);
    try {
      const { data } = await api.get(`/superadmin/customer-identity/clients/${row.tenant_id}/duplicate-audit`);
      setActivateSnapshot(data);
    } catch (e) { /* snapshot is informational only — a failure here never blocks the activation form itself */ }
    finally { setActivateSnapshotBusy(false); }
  };
  const closeActivate = () => { setActivateTarget(null); setActivateResult(null); setActivateErr(""); setActivateSnapshot(null); };

  const confirmActivate = async () => {
    setActivateErr("");
    if (!af.reason.trim()) { setActivateErr("A reason is required."); return; }
    if (!af.password) { setActivateErr("Your Super Admin password is required."); return; }
    setActivateBusy(true);
    try {
      const { data } = await api.post(`/superadmin/customer-identity/clients/${activateTarget.tenant_id}/activate`,
        { reason: af.reason.trim(), password: af.password, code: af.code || null });
      setActivateResult(data);
      toast.success(data.already_on ? "Already ON for this tenant — no change made" : `Customer identity enforcement activated for ${activateTarget.business_name}`);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      setActivateErr(apiError(typeof detail === "object" ? detail?.message : detail));
    } finally { setActivateBusy(false); }
  };

  const runAudit = async (row) => {
    setAuditTarget(row); setAuditData(null); setAuditErr(""); setAuditBusy(true);
    try {
      const { data } = await api.get(`/superadmin/customer-identity/clients/${row.tenant_id}/duplicate-audit`);
      setAuditData(data);
    } catch (e) { setAuditErr(apiError(e.response?.data?.detail)); }
    finally { setAuditBusy(false); }
  };
  const closeAudit = () => { setAuditTarget(null); setAuditData(null); setAuditErr(""); };

  const confirmDisable = async () => {
    setErr("");
    if (!f.reason.trim()) { setErr("A reason is required."); return; }
    if (!f.password) { setErr("Your Super Admin password is required."); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/superadmin/customer-identity/clients/${target.tenant_id}/emergency-disable`,
        { reason: f.reason.trim(), password: f.password, code: f.code || null });
      setResult(data);
      toast.success(data.already_off ? "Already OFF for this tenant — no change made" : `Customer identity enforcement disabled for ${target.business_name}`);
      load();
    } catch (e) { setErr(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (rows === null) return null;

  return (
    <section className="pt-2" data-testid="ci-panel">
      <div className="flex items-center gap-2 mb-1">
        <Fingerprint className="w-5 h-5 text-sky-600" />
        <h2 className="font-head text-xl font-extrabold">Customer Identity Protection</h2>
        <Badge variant="outline">{rows.length}</Badge>
      </div>
      <p className="text-xs text-muted-foreground mb-3">
        Per-tenant duplicate-customer safeguard (POS-0034). Super Admin is the only role that can turn this ON or OFF —
        activation is hard-gated on both identity indexes being ready, and emergency disable is always available and fully audited.
      </p>
      {rows.length > 0 && (
        <div className="relative mb-3 max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search by business name or tenant ID…" className="pl-9" data-testid="ci-search" />
        </div>
      )}
      {rows.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="ci-empty">No POS tenants found.</Card>
      ) : filteredRows.length === 0 ? (
        <Card className="p-6 text-center text-sm text-muted-foreground" data-testid="ci-no-search-results">No tenants match "{search}".</Card>
      ) : (
        <>
        <Card className="divide-y divide-border" data-testid="ci-rows">
          {pageRows.map((row) => (
            <div key={row.tenant_id} className="p-3 flex flex-wrap items-center justify-between gap-2" data-testid={`ci-row-${row.tenant_id}`}>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <p className="font-medium text-sm truncate" data-testid={`ci-name-${row.tenant_id}`}>{row.business_name} <span className="text-[10px] font-normal text-muted-foreground mono" data-testid={`ci-tenant-id-${row.tenant_id}`}>#{row.tenant_id.slice(0, 8)}</span></p>
                  {row.enforcement_enabled ? (
                    <Badge className="bg-rose-500/15 text-rose-600 border-rose-500/30 text-[10px] flex items-center gap-1" data-testid={`ci-status-on-${row.tenant_id}`}><ShieldCheck className="w-3 h-3" /> ENFORCEMENT ON</Badge>
                  ) : (
                    <Badge className="bg-slate-500/10 text-slate-500 border-slate-500/20 text-[10px] flex items-center gap-1" data-testid={`ci-status-off-${row.tenant_id}`}><ShieldOff className="w-3 h-3" /> OFF</Badge>
                  )}
                  <ReadinessBadge ready={row.readiness_ready} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5" data-testid={`ci-last-change-${row.tenant_id}`}>
                  {row.last_change
                    ? `Last change: ${row.last_change.new_state ? "Enabled" : "Disabled"} by ${row.last_change.changed_by} · ${fmt(row.last_change.created_at)}${row.last_change.reason ? ` · "${row.last_change.reason}"` : ""}`
                    : "No enforcement change on record for this tenant."}
                </p>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" onClick={() => runAudit(row)} data-testid={`ci-audit-btn-${row.tenant_id}`}>
                  <Search className="w-3.5 h-3.5 mr-1.5" /> Run Read-Only Audit
                </Button>
                {row.enforcement_enabled ? (
                  <Button size="sm" variant="outline" className="border-rose-500/40 text-rose-600" onClick={() => openDisable(row)} data-testid={`ci-disable-btn-${row.tenant_id}`}>
                    <ShieldAlert className="w-3.5 h-3.5 mr-1.5" /> Emergency Disable
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" className="border-emerald-500/40 text-emerald-600" onClick={() => openActivate(row)} data-testid={`ci-activate-btn-${row.tenant_id}`}>
                    <ShieldPlus className="w-3.5 h-3.5 mr-1.5" /> Activate Enforcement
                  </Button>
                )}
              </div>
            </div>
          ))}
        </Card>
        <div className="flex items-center justify-between gap-3 mt-3 flex-wrap">
          <p className="text-xs text-muted-foreground" data-testid="ci-pagination-summary">
            Showing {(page - 1) * PAGE_SIZE + 1}-{Math.min(page * PAGE_SIZE, filteredRows.length)} of {filteredRows.length}{search.trim() ? ` (filtered from ${rows.length})` : ""}
          </p>
          {totalPages > 1 && (
            <Pagination className="mx-0 w-auto" data-testid="ci-pagination">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious href="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.max(1, p - 1)); }} className={page === 1 ? "pointer-events-none opacity-40" : ""} data-testid="ci-page-prev" />
                </PaginationItem>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((n) => (
                  <PaginationItem key={n}>
                    <PaginationLink href="#" isActive={n === page} onClick={(e) => { e.preventDefault(); setPage(n); }} data-testid={`ci-page-${n}`}>{n}</PaginationLink>
                  </PaginationItem>
                ))}
                <PaginationItem>
                  <PaginationNext href="#" onClick={(e) => { e.preventDefault(); setPage((p) => Math.min(totalPages, p + 1)); }} className={page === totalPages ? "pointer-events-none opacity-40" : ""} data-testid="ci-page-next" />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
        </>
      )}

      <Dialog open={!!target} onOpenChange={(o) => { if (!o) closeDialog(); }}>
        <DialogContent className="max-w-lg" data-testid="ci-disable-modal">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><ShieldAlert className="w-5 h-5 text-rose-500" /> Emergency Disable — Customer Identity Enforcement</DialogTitle>
            <DialogDescription>Turns the database-backed duplicate-customer protection OFF for one tenant only. Re-enabling requires the separate, approved migration process.</DialogDescription>
          </DialogHeader>

          {result ? (
            <div className="space-y-3" data-testid="ci-disable-result">
              <div className="rounded-xl border-2 border-emerald-500/50 bg-emerald-500/10 p-4 text-sm space-y-1">
                <p className="font-bold text-emerald-600">{result.already_off ? "Already OFF — no change made" : "Enforcement disabled"}</p>
                <p>Tenant: <b>{result.business_name || target?.business_name}</b></p>
                {!result.already_off && <p>Reason recorded: <b>{result.reason}</b></p>}
                {!result.already_off && <p>Changed by: <b>{result.changed_by}</b> at {fmt(result.changed_at)}</p>}
              </div>
              <p className="text-xs text-muted-foreground" data-testid="ci-disable-limitations">
                Existing customer records and their claim keys are unchanged — nothing was deleted and no index was dropped.
                Checkout, credit and Wallet are unaffected. Until enforcement is re-enabled through the approved migration
                process, new registrations only have the non-atomic duplicate-phone warning, not the database-level guarantee.
              </p>
              <Button className="w-full" variant="outline" onClick={closeDialog} data-testid="ci-disable-done">Done</Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="ci-disable-form">
              <div className="rounded-lg border border-rose-500/40 bg-rose-500/5 p-3 text-sm flex gap-2">
                <Database className="w-5 h-5 text-rose-500 shrink-0" />
                <p>This affects <b>{target?.business_name}</b> (tenant <code className="mono text-xs bg-muted px-1 rounded" data-testid="ci-disable-target-id">{target?.tenant_id}</code>) only. Other tenants are never changed by this action.</p>
              </div>
              <div>
                <Label className="text-xs">Reason (required, recorded in the audit trail)</Label>
                <Textarea value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} rows={3} data-testid="ci-disable-reason" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label className="text-xs">Your Super Admin password</Label><Input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="ci-disable-password" /></div>
                <div><Label className="text-xs">Authenticator code</Label><Input value={f.code} onChange={(e) => setF({ ...f, code: e.target.value.trim() })} className="mono" data-testid="ci-disable-code" /></div>
              </div>
              {err && <p className="text-sm text-rose-500" data-testid="ci-disable-error">{err}</p>}
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={closeDialog} data-testid="ci-disable-cancel">Cancel</Button>
                <Button className="bg-rose-600 hover:bg-rose-700 text-white" disabled={busy} onClick={confirmDisable} data-testid="ci-disable-confirm">{busy ? "Disabling…" : "Disable Enforcement"}</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!activateTarget} onOpenChange={(o) => { if (!o) closeActivate(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ci-activate-modal">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><ShieldPlus className="w-5 h-5 text-emerald-600" /> Activate — Customer Identity Enforcement</DialogTitle>
            <DialogDescription>Turns the database-backed duplicate-customer protection ON for one tenant only.</DialogDescription>
          </DialogHeader>

          {activateResult ? (
            <div className="space-y-3" data-testid="ci-activate-result">
              <div className="rounded-xl border-2 border-emerald-500/50 bg-emerald-500/10 p-4 text-sm space-y-1">
                <p className="font-bold text-emerald-600">{activateResult.already_on ? "Already ON — no change made" : "Enforcement activated"}</p>
                <p>Tenant: <b>{activateResult.business_name || activateTarget?.business_name}</b></p>
                {!activateResult.already_on && <p>Reason recorded: <b>{activateResult.reason}</b></p>}
                {!activateResult.already_on && <p>Changed by: <b>{activateResult.changed_by}</b> at {fmt(activateResult.changed_at)}</p>}
                {!activateResult.already_on && typeof activateResult.legacy_duplicate_groups === "number" && (
                  <p>Legacy duplicate groups at activation: <b>{activateResult.legacy_duplicate_groups}</b> (left exactly as-is, not touched)</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground" data-testid="ci-activate-limitations">
                No existing customer was changed, merged or deleted. Legacy duplicate-phone records remain fully accessible,
                editable and billable — only a brand-new create, or an edit submitted from now on, gets the atomic
                duplicate-prevention guarantee. Checkout, credit and Wallet are unaffected.
              </p>
              <Button className="w-full" variant="outline" onClick={closeActivate} data-testid="ci-activate-done">Done</Button>
            </div>
          ) : (
            <div className="space-y-3" data-testid="ci-activate-form">
              {!activateTarget?.readiness_ready ? (
                <div className="rounded-lg border-2 border-rose-500/50 bg-rose-500/10 p-4 text-sm flex gap-2" data-testid="ci-activate-not-ready">
                  <AlertTriangle className="w-5 h-5 text-rose-500 shrink-0" />
                  <p>Cannot activate yet — both identity indexes (<code className="mono text-xs">nx3_customers_phone_claim_key</code>, <code className="mono text-xs">nx3_customer_phone_overrides_approval_id</code>) must be installed and ready for <b>{activateTarget?.business_name}</b> first. Install them via the index-maintenance endpoint, confirm readiness, then retry.</p>
                </div>
              ) : (
                <>
                  <div className="rounded-lg border border-emerald-500/40 bg-emerald-500/5 p-3 text-sm flex gap-2">
                    <Database className="w-5 h-5 text-emerald-600 shrink-0" />
                    <p>This affects <b>{activateTarget?.business_name}</b> (tenant <code className="mono text-xs bg-muted px-1 rounded" data-testid="ci-activate-target-id">{activateTarget?.tenant_id}</code>) only. Other tenants are never changed by this action.</p>
                  </div>
                  {activateSnapshotBusy && <p className="text-xs text-muted-foreground flex items-center gap-2" data-testid="ci-activate-snapshot-loading"><Loader2 className="w-3.5 h-3.5 animate-spin" /> Checking legacy duplicates…</p>}
                  {activateSnapshot && activateSnapshot.duplicate_canonical_groups > 0 && (
                    <div className="rounded-lg border border-amber-500/40 bg-amber-500/5 p-3 text-xs flex gap-2" data-testid="ci-activate-legacy-warning">
                      <AlertTriangle className="w-4 h-4 text-amber-600 shrink-0" />
                      <p><b>{activateSnapshot.duplicate_canonical_groups} known legacy duplicate-phone group(s)</b> exist for this tenant. Activation does not block on this — those records are unaffected and remain fully accessible; only new creates/edits from now on get the atomic guarantee.</p>
                    </div>
                  )}
                  <div>
                    <Label className="text-xs">Reason (required, recorded in the audit trail)</Label>
                    <Textarea value={af.reason} onChange={(e) => setAf({ ...af, reason: e.target.value })} rows={3} data-testid="ci-activate-reason" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div><Label className="text-xs">Your Super Admin password</Label><Input type="password" value={af.password} onChange={(e) => setAf({ ...af, password: e.target.value })} data-testid="ci-activate-password" /></div>
                    <div><Label className="text-xs">Authenticator code</Label><Input value={af.code} onChange={(e) => setAf({ ...af, code: e.target.value.trim() })} className="mono" data-testid="ci-activate-code" /></div>
                  </div>
                  {activateErr && <p className="text-sm text-rose-500" data-testid="ci-activate-error">{activateErr}</p>}
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={closeActivate} data-testid="ci-activate-cancel">Cancel</Button>
                    <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={activateBusy} onClick={confirmActivate} data-testid="ci-activate-confirm">{activateBusy ? "Activating…" : "Activate Enforcement"}</Button>
                  </div>
                </>
              )}
              {!activateTarget?.readiness_ready && (
                <Button className="w-full" variant="outline" onClick={closeActivate} data-testid="ci-activate-close-not-ready">Close</Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!auditTarget} onOpenChange={(o) => { if (!o) closeAudit(); }}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="ci-audit-modal">
          <DialogHeader>
            <DialogTitle className="font-head flex items-center gap-2"><Search className="w-5 h-5 text-sky-600" /> Read-Only Duplicate Audit</DialogTitle>
            <DialogDescription>{auditTarget?.business_name} — scans this tenant only. No writes, no index changes, no customer data modified.</DialogDescription>
          </DialogHeader>
          {auditBusy && <p className="text-sm text-muted-foreground flex items-center gap-2 py-6 justify-center" data-testid="ci-audit-loading"><Loader2 className="w-4 h-4 animate-spin" /> Scanning…</p>}
          {auditErr && <p className="text-sm text-rose-500" data-testid="ci-audit-error">{auditErr}</p>}
          {auditData && (
            <div className="space-y-3" data-testid="ci-audit-result">
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg border border-border p-2" data-testid="ci-audit-scanned"><p className="text-xl font-head font-extrabold">{auditData.scanned_customers}</p><p className="text-[10px] text-muted-foreground">Scanned</p></div>
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-2" data-testid="ci-audit-dup-groups"><p className="text-xl font-head font-extrabold">{auditData.duplicate_canonical_groups}</p><p className="text-[10px] text-muted-foreground">Duplicate Groups</p></div>
                <div className="rounded-lg border border-border p-2" data-testid="ci-audit-invalid"><p className="text-xl font-head font-extrabold">{(auditData.phone_status_counts?.invalid || 0) + (auditData.phone_status_counts?.contains_letters || 0) + (auditData.phone_status_counts?.has_extension || 0)}</p><p className="text-[10px] text-muted-foreground">Invalid Numbers</p></div>
              </div>
              {auditData.truncated && <p className="text-xs text-amber-600" data-testid="ci-audit-truncated">Scan stopped at the safety limit — results are partial. Re-run during a quieter window or raise the limit.</p>}
              <p className="text-xs text-muted-foreground">Customers in a duplicate group: <b>{auditData.customers_in_duplicate_groups}</b> · Index readiness: <ReadinessBadge ready={auditData.readiness_ready} /> · Scanned {fmt(auditData.scanned_at)} ({auditData.elapsed_ms}ms)</p>
              {auditData.sample_duplicate_groups?.length > 0 && (
                <div className="space-y-1" data-testid="ci-audit-sample">
                  <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Sample groups (phone masked, no names)</p>
                  {auditData.sample_duplicate_groups.map((g, i) => (
                    <div key={i} className="text-xs rounded-md border border-border p-2 flex justify-between" data-testid={`ci-audit-group-${i}`}>
                      <span className="mono">{g.canonical_masked}</span><span>{g.customer_count} customers</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="text-[11px] text-muted-foreground" data-testid="ci-audit-note">{auditData.note}</p>
              <Button className="w-full" variant="outline" onClick={closeAudit} data-testid="ci-audit-done">Done</Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
}
